import moment from 'moment';

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Col, Modal, ProgressBar, Row } from 'react-bootstrap';
import { DropdownInput } from '../../../shared/form/FormInput';
import { ActivityIcon, BrainIcon, ChartPieIcon, DateIcon, EmailIcon, FileTextIcon, HeartIcon, LocationIcon, PhoneIcon, ResultIcon, UserIcon } from '../../../shared/icons/Icons';
import { responderCountList, runAssessmentClientList, runAssessmentList, submitDetail, resultCsvDownload, resultDetail } from '../../../shared/settings/apiConfig';
import { getApi, postApi } from '../../../shared/services/apiHelper';

import { PHQ9_RANGES, RANGE_BY_ASSESSMENT } from '../../../utils/utils';

import './AssignmentView.scss';
import { QUESTION_TYPE } from '../../../shared/utils/constants';
import CodeViewer from '../../../shared/components/CodeViewer';
import { renderImageFromView } from '../take-assessments/QuestionRenderer';

const domainsDropdown = [
    { id: 0, value: "Overview" },
    { id: 1, value: "Responses" },
    { id: 2, value: "Analysis" }
];

const MAX_SCORE = 3;

const AssignmentView = (props) => {

    const { assignmentId } = useParams();

    const user = useSelector((state) => { return state.user });

    const [assessments, setAssessments] = useState([]);
    const [selectedAssessment, setSelectedAssessment] = useState('');
    const [runAssessmentClientData, setRunAssessmentClientData] = useState([]);
    const [responderCountData, setResponderCountData] = useState([]);
    const [assignmentModal, setAssignmentModal] = useState({ show: false, data: null });
    const [submissionModal, setSubmissionModal] = useState({ show: false, data: null });
    
    const [responderDetails, setResponderDetails] = useState(null);
    const [responderName, setResponderName] = useState('');
    const [selectedResponderIndex, setSelectedResponderIndex] = useState(null);

    const [activeDomain, setActiveDomain] = useState("Overview");

    const customResponseCount = (item) => {
        const count = responderCountData.find(elem => elem.clientid == item.clientid)?.respondercount || 0;
        return (
            <span className={'responses-badge ' + (count ? `clickable` : '')} onClick={count > 0 ? () => { getResponderDetails(item) } : null}>
                {count} responses
            </span>
        );
    };

    // const customStatus = (value) => {
    //     const statusStyles = {
    //         'submitted': { backgroundColor: '#f5f5f5', color: '#666666', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' },
    //         'pending': { backgroundColor: '#fff3e0', color: '#e65100', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' },
    //         'overdue': { backgroundColor: '#ffebee', color: '#c62828', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' }
    //     };
    //     const style = statusStyles[value] || { padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '500' };
    //     return <span style={style}>{value || 'pending'}</span>;
    // };

    useEffect(() => {
        getRunAssessmentList();
    }, []);

    useEffect(() => {
        if ( selectedAssessment ) {
            Promise.all([getRunAssessmentClientList(selectedAssessment), getResponderCountList(selectedAssessment)]).then(responses => {
                setRunAssessmentClientData(responses[0].data);
                setResponderCountData(responses[1].data);
            }).catch(error => {
                console.log(error);
            });
        }
    }, [selectedAssessment]);

    const getRunAssessmentList = () => {
        let payload = {
            "field": ["id", "runassessmentid", "runid", "assessmentid", "assessmentname", "assessmentcategory"],
            "filter": [
                {
                    "field": "runid",
                    "value": assignmentId,
                    "op": "=",
                    "join": "AND"
                }
            ]
        }
        postApi(runAssessmentList, payload).then(response => {
            if (response.data.data && response.data.data.length > 0) {
                // Format assessments for dropdown
                const assessmentList = response.data.data.map(item => ({ id: item.id, value: item.assessmentname }));
                setAssessments(assessmentList);

                // Set first assessment as selected
                const firstAssessmentData = response.data.data[0];
                setSelectedAssessment(firstAssessmentData);
            }
        }).catch(error => {
            console.log(error);
        })
    };

    const getRunAssessmentClientList = async (runAssessment) => {
        let payload = {
            field: [ 'id', 'runstatus', 'starttime', 'endtime', 'runassessmentid', 'runid', 'assessmentid', 'assessmentname', 'clientid', 'clientemail', 'firstname', 'lastname' ],
            filter: [
                { field: 'runid', value: parseInt(assignmentId), op: '=', join: 'AND' },
                { field: 'runassessmentid', value: runAssessment.id, op: '=', join: 'AND' },
                { field: 'responderid', value: null, op: 'IS', join: 'AND' }
            ]
        };

        try {
            const response = await postApi(runAssessmentClientList, payload);
            return response.data;
        } catch (error) {
            throw error;
        };
    };

    const getResponderCountList = async (runAssessment) => {
        let payload = {
            runid: assignmentId,
            runassessmentid: runAssessment.id
        };

        try {
            const response = await postApi(responderCountList, { data: payload });
            return response.data;
        } catch (error) {
            throw error;
        };
    };

    const handleAssessmentChange = (value) => {
        setSelectedAssessment(value);
    };

    const getResponderDetails = async (row) => {
        try {
            let payload = {
                "field": ["id", "runstatus", "starttime", "endtime", "responderid", "responderdetails", "resultid", "score", "answers", "totalscore", "severity", "interpretation"],
                "filter": [
                    { "field": "runid", "value": assignmentId, "op": "=", "join": "AND" },
                    { "field": "runassessmentid", "value": selectedAssessment.id, "op": "=", "join": "AND" },
                    { "field": "clientid", "value": row.clientid, "op": "=", "join": "AND" },
                    { "field": "responderid", "value": null, "op": "IS NOT", "join": "AND" },
                    { "field": "runstatus", "value": 'Completed', "op": "=", "join": "AND" }
                ]
            }
            const response = await postApi(runAssessmentClientList, payload);

            const parsedData = response.data.data.map(item => ({
                ...item,
                responderdetails: item.responderdetails ? JSON.parse(item.responderdetails) : null
            }));

            setResponderDetails(parsedData);

            const responderName = parsedData.map((item, index) => ({ 
                id: item.id, 
                value: item.responderdetails.fullname 
            }));
            setResponderName(responderName);
            setSelectedResponderIndex(parsedData[0].id);

            if ( selectedAssessment.assessmentcategory != 'psychological' ) {
                const resultResponse = await getResultDetail(parsedData[0].resultid);
                setAssignmentModal({ show: true, data: { responderData: parsedData, rowData: row, templateHtml: resultResponse.data[0]['answers'] } });
            } else {
                setAssignmentModal({ show: true, data: { responderData: parsedData, rowData: row } });
            }
        } catch (error) {
            console.log(error);
        }
    }

    const getResultDetail = async (resultId) => {
        try {
            const response = await getApi(resultDetail + resultId);
            return response.data;
        } catch (error) {
            throw error;
        }
    };

    const getTimeSpent = (startTime, endTime) => {
        const start = moment(startTime);
        const end = moment(endTime);

        const duration = moment.duration(end.diff(start));

        const minutes = Math.floor(duration.asMinutes());
        const seconds = duration.seconds();
        return (
            <Card className="card-view">
                <h5>Time Spent</h5>
                <h6>{minutes || 0} min {seconds || 0} sec</h6>
            </Card>
        );
    }

    const getAnswers = (answers) => {
        if (!answers || !answers.sections) return null;

        return answers.sections.flatMap((section, index) =>
            section.questions.map((question, qIndex) => {
                const percentage = (question.score / MAX_SCORE) * 100;
                const selectedOptions = question.options?.filter(opt => opt.answered);

                return (
                    <div className="question-row" key={question.id}>
                        <div className="question-left">
                            <span className="question-index">{qIndex + 1}</span>
                            <div className="d-flex justify-content-between align-items-start w-100 gap-3">
                                <div className="question-text flex-grow-1">
                                    {question.questionname}
                                </div>

                                <div className="text-nowrap">
                                    <span className="text-l fw-bold">{question.score}</span><span className="text-n text-muted">/{MAX_SCORE}</span>
                                </div>
                            </div>
                        </div>

                        <div className="question-right gap-3">
                            <div className="d-flex w-80 mt-3 ms-3">
                                <ProgressBar now={percentage} min={0} max={100} style={{ height: "0.5rem" }} />
                            </div>
                            <div className="score-text w-20">
                                <span className="answer-label">
                                    {selectedOptions?.[0]?.optionname || "No Answer"}
                                </span>
                            </div>
                        </div>
                    </div>
                );

            })
        );
    };

    const closeModal = () => {
        setAssignmentModal({ show: false, data: null });
        setSubmissionModal({ show: false, data: null });
        setActiveDomain('Overview');
    };

    const getSubmitAnalysis = (runAssessmentClientId) => {
        getApi(submitDetail + runAssessmentClientId).then(response => {
            if ( Array.isArray(response.data.data) && response.data.data.length > 0 ) {
                setSubmissionModal(prevState => {
                    prevState.show = true;
                    prevState.data = response.data.data[0];
                    return {...prevState};
                });
            }
        }).catch(error => {
            console.log(error);
        });
    };

    const downloadAsCsv = (item) => {
        let payload = {
            "runassessmentid": item.runassessmentid,
            "clientid": item.clientid,
            "status": "Completed"
        };

        postApi(resultCsvDownload, {data: payload}).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `responses_${item.runassessmentid}_${Date.now()}.csv`); // filename
            
            document.body.appendChild(link);
            
            link.click();
            link.remove();
        }).catch(error => {
            console.log(error);
        });
    };

    const responder = responderDetails?.find(r => r.id === selectedResponderIndex);

    const getAssessmentKey = (assessmentName = "") => {
        if (assessmentName.includes("PHQ-9")) return "PHQ-9";
        if (assessmentName.includes("GAD-7")) return "GAD-7";
        if (assessmentName.includes("BAI")) return "BAI";
        if (assessmentName.includes("BDI-II")) return "BDI-II";
        return "PHQ-9"; 
    };

    const assessmentKey = getAssessmentKey(selectedAssessment.assessmentname);

    const ranges = RANGE_BY_ASSESSMENT[assessmentKey] || PHQ9_RANGES;

    const score = Number(responder?.score ?? 0);

    const activeRange = ranges.find(r => score >= r.min && score <= r.max) || ranges[0];

    const getResponseDistribution = (questions) => {
        const scoreMap = {};
        const scoreCount = {};

        questions.forEach(q => {
            q.options.forEach(opt => {
                if (!scoreMap[opt.score]) {
                    scoreMap[opt.score] = opt.optionname;
                }
                if (scoreCount[opt.score] === undefined) {
                    scoreCount[opt.score] = 0;
                }
            });
        });

        questions.forEach(q => {
            const selected = q.options.find(o => o.answered);
            if (selected) {
                scoreCount[selected.score]++;
            }
        });

        const total = questions.length || 1;

        return Object.keys(scoreMap)
            .sort((a, b) => Number(a) - Number(b))
            .map(score => ({
                score: Number(score),
                label: scoreMap[score],
                count: scoreCount[score] || 0,
                percentage: Math.round((scoreCount[score] / total) * 100)
            }));
    };

    return (
        <div className='main-content assignment-view-container'>
            <div className='d-flex flex-column'>
                {/* <button
                    type="button"
                    className="btn-back"
                    onClick={() => props.history.push('/assignments')}
                >
                    <Back />
                </button> */}
                <h4 className='fw-bold'>Assignment Results & Analytics</h4>
                <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-2'>
                    <h6 className='text-muted-label m-0'>View student performance and provide feedback</h6>
                    <div style={{width: '30%'}} className='dropdown'>
                        <DropdownInput
                            name='assessment'
                            defaultValue={assessments.length > 0 ? assessments[0].value : "Select Assessment"}
                            value={selectedAssessment}
                            options={assessments}
                            onChange={handleAssessmentChange}
                        />
                    </div>
                </div>
            </div>

            <div className="row g-3">
                {
                    runAssessmentClientData.map(item => (
                        <div className="col-md-6 col-lg-4">
                            <div className='card-view h-100'>
                                <div className="card-body">
                                    <div className='d-flex justify-content-between'>
                                        <div className='d-flex flex-column'>
                                            <strong>Name</strong>
                                            <span className="card-title">{item.firstname || ''} {item.lastname || ''}</span>
                                        </div>
                                        <div className='d-flex' style={{ height: "fit-content" }}>
                                            <span className="badge bg-warning">{customResponseCount(item)}</span>
                                        </div>
                                    </div>
                                    <div className='d-flex flex-column'>
                                        <strong>Email</strong>
                                        <span className="text-muted">{item.clientemail}</span>
                                    </div>
                                    <div className='d-flex flex-column'>
                                        <strong>Submission Date</strong>
                                        <span>{item.endtime ? moment.utc(item.endtime).format('MMM DD, YYYY') : '- - - -'}</span>
                                    </div>
                                    <div className='d-flex flex-column'>
                                        <strong>Status </strong>
                                        <span>{item.runstatus}</span>
                                    </div>
                                    <div className='d-flex flex-column justify-content-end flex-sm-row flex-lg-column flex-xxl-row gap-2'>
                                        <div className="d-flex align-items-center gap-2">
                                            {
                                                item.runstatus === 'Completed' ?
                                                    <button className="btn btn-outline-primary btn-sm">
                                                        <div className='cursor-pointer' style={{ width: 'max-content' }} onClick={() => getSubmitAnalysis(item.id)}>View {user?.rolename?.includes("Assesse") ? 'Your' : 'Student'} Responses</div>
                                                    </button> : ""
                                            }
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {
                                                item.runstatus === 'Completed' ?
                                                    <button className="btn btn-outline-secondary btn-sm">
                                                        <div className='cursor-pointer' style={{ width: 'max-content' }} onClick={() => downloadAsCsv(item)}>Download Responses</div>
                                                    </button> : ""
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    ))
                }
            </div>

            {assignmentModal.show &&
                <Modal show={assignmentModal.show} onHide={closeModal} centered size="lg" dialogClassName="assessment-modal">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <div>
                                <div className="fw-bold d-flex align-items-center gap-2">
                                    <UserIcon width="25px" />Assessment Responses
                                </div>
                            </div>
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body style={{ overflowY: "auto" }}>
                        {
                            !responderDetails || responderDetails.length === 0 ? 
                            (
                                <p>No respondent details found.</p>
                            ) : 
                            ( 
                                <>
                                    {responderDetails.length > 1 && (
                                        <div className='d-flex gap-2 align-items-center'>
                                            <p>Select a responder:</p>
                                            <div className="mb-3" style={{ width: "250px" }}>
                                                <DropdownInput name='responder' value={selectedResponderIndex} options={responderName} 
                                                    onChange={(value) => {
                                                        setSelectedResponderIndex(parseInt(value))
                                                        setActiveDomain('Overview');

                                                        const responder = responderDetails?.find(r => r.id === parseInt(value));

                                                        if ( responder ) {
                                                            getResultDetail(responder.resultid).then(response => {
                                                                setAssignmentModal(prevState => {
                                                                    prevState.data = {...prevState.data, templateHtml: response.data[0]['answers']};
                                                                    return {...prevState};
                                                                });
                                                            }).catch(error => {
                                                                console.log(error);
                                                            });
                                                        }   
                                                    }
                                                } />
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <Card className="info-card mb-4">
                                            <Card.Body>
                                                <h6 className="mb-3 fw-bold"> <UserIcon width="15px" /> Respondent Information</h6>
                                                <Row className='respondent-card'>
                                                    <Col md={4}>
                                                        <div className='d-flex align-items-center gap-3'>
                                                            <div className='icon-border icon-user'>
                                                                <UserIcon size="15px" />
                                                            </div>
                                                            <div className='d-flex flex-column mb-3'>
                                                                <strong className='text-nowrap'>Full Name</strong>
                                                                <div>{responder.responderdetails.fullname}</div>
                                                            </div>
                                                        </div>
                                                        <div className='d-flex align-items-center gap-3'>
                                                            <div className='icon-border icon-location'>
                                                                <LocationIcon size="30px" />
                                                            </div>
                                                            <div className='d-flex flex-column'>
                                                                <strong className='text-nowrap'>Address</strong>
                                                                <div>{responder.responderdetails.address}</div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={4}>
                                                        <div className='d-flex align-items-center gap-3'>
                                                            <div className='icon-border icon-email'>
                                                                <EmailIcon size="15px" />
                                                            </div>
                                                            <div className='d-flex flex-column mb-3'>
                                                                <strong className='text-nowrap'>Email Address</strong>
                                                                <div>{responder.responderdetails.email}</div>
                                                            </div>
                                                        </div>
                                                        <div className='d-flex align-items-center gap-3'>
                                                            <div className='icon-border icon-date'>
                                                                <DateIcon size="15px" />
                                                            </div>
                                                            <div className='d-flex flex-column'>
                                                                <strong className='text-nowrap'>Date of Birth</strong>
                                                                <div>{responder.responderdetails.dateofbirth}</div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col md={4}>
                                                        <div className='d-flex align-items-center gap-3'>
                                                            <div className='icon-border icon-phone'>
                                                                <PhoneIcon size="15px" />
                                                            </div>
                                                            <div className='d-flex flex-column mb-3'>
                                                                <strong className='text-nowrap'>Phone Number</strong>
                                                                <div>{responder.responderdetails.mobile}</div>
                                                            </div>
                                                        </div>
                                                        <div className='d-flex align-items-center gap-3'>
                                                            <div className='icon-border icon-heart'>
                                                                <HeartIcon size="15px" />
                                                            </div>
                                                            <div className='d-flex flex-column'>
                                                                <strong className='text-nowrap'>Marital Status</strong>
                                                                <div>{responder.responderdetails.martialstatus}</div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Card.Body>
                                        </Card>

                                        {
                                            selectedAssessment.assessmentcategory == 'psychological' ?
                                            <>
                                                <div className="overview-domain mt-4">
                                                    <Row className="mt-3 mb-4 domain-mobile">
                                                        <Col md={4}>
                                                            <Card className="card-view">
                                                                <div className="text-primary fw-bold" style={{ fontSize: "20px" }}>{responder.score ? responder.score : '- - -'}</div>
                                                                <h5>Total Score</h5>
                                                            </Card>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Card className="card-view">
                                                                <div style={{ color: activeRange.color, fontSize: "20px" }}>{responder?.severity || '- - -'}</div>
                                                                <h5>Severity</h5>
                                                            </Card>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Card className="card-view">
                                                                <div className="text-primary fw-bold" style={{ fontSize: "20px" }}>
                                                                    {responder?.answers ? JSON.parse(responder.answers)?.sections?.flatMap(s => s.questions)?.length : '- - -'}
                                                                </div>
                                                                <h5>Questions</h5>
                                                                {/* <div>
                                                            <Badge bg={responder.runstatus === "Completed" ? "success pd-7" : "secondary pd-7"}>{responder.runstatus}</Badge>
                                                        </div> */}
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </div>

                                                <div className="gap-2 domain-tabs w-100" style={{ flexWrap: "nowrap !important" }}>
                                                    {domainsDropdown.map((domain) => {
                                                        const isActive = activeDomain === domain.value;

                                                        return (
                                                            <div key={domain.id} onClick={() => setActiveDomain(domain.value)} className={`d-flex align-items-center justify-content-center w-25 domain-tab ${isActive ? "active" : ""}`}>
                                                                {domain.value}
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {
                                                    activeDomain === "Overview" && (
                                                        <>
                                                            <div className="d-flex flex-column p-3 user-input-seaction">
                                                                <div className="d-flex gap-2">
                                                                    <ResultIcon color="hsl(278, 80%, 36%)" /><h5 className="font-weight-bold">Score Breakdown</h5>
                                                                </div>
                                                                <div className="d-flex justify-content-between mt-3">
                                                                    <div className="text-muted font-weight-bold text-n mb-3">Score Progress</div>
                                                                    <div>
                                                                        {responder.score > 0 ? `${Math.round((responder.score / (responder.totalscore || 100)) * 100)}%` : "0%"}
                                                                    </div>
                                                                </div>
                                                                <ProgressBar className="d-flex align-items-center" now={responder.score} min={0} max={responder.totalscore} label={`${responder.score} / ${responder.totalscore || 100}`} style={{ height: "1rem", backgroundColor: `${activeRange.color} !important` }} />

                                                                <div className="mt-3">
                                                                    <div className="text-muted font-weight-bold text-n mb-3">Score Range</div>
                                                                    <ProgressBar className="score-range-bar">
                                                                        <ProgressBar now={4} max={27} className="range-green" key={1} />
                                                                        <ProgressBar now={5} max={27} className="range-blue" key={2} />
                                                                        <ProgressBar now={5} max={27} className="range-orange" key={3} />
                                                                        <ProgressBar now={5} max={27} className="range-yellow" key={4} />
                                                                        <ProgressBar now={8} max={27} className="range-red" key={5} />
                                                                    </ProgressBar>
                                                                </div>
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">0</span>
                                                                    <span className="text-muted">{responder.totalscore}</span>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex flex-column gap-2 border rounded p-3 user-input-seaction">
                                                                <div className="d-flex gap-2">
                                                                    <BrainIcon color="hsl(28, 92%, 53%)" /><h5 className="font-weight-bold">Clinical Interpretation</h5>
                                                                </div>
                                                                <div className="d-flex gap-2 rounded-2" style={{ backgroundColor: activeRange.bg, color: activeRange.color, padding: "10px" }}>
                                                                    <ActivityIcon color={activeRange.color} />
                                                                    <div className="fw-bold" color={activeRange.color}>{responder.severity || 'None'}</div>
                                                                </div>
                                                                <div className="text-n text-muted">{responder?.interpretation || 'No interpretation available'}</div>
                                                            </div>
                                                        </>
                                                    )
                                                }

                                                {
                                                    activeDomain === "Responses" && (
                                                        <div className="responses-domain mt-4">
                                                            {responder.answers ? getAnswers(JSON.parse(responder.answers)) : <p className="text-muted">No responses available.</p>}
                                                        </div>
                                                    )
                                                }

                                                {
                                                    activeDomain === "Analysis" && (
                                                        <div className="d-flex flex-row gap-3 g-5 mt-3">
                                                            <div className="d-flex flex-column gap-2 col-md-6 response-section p-3 content-card">
                                                                <div className="d-flex gap-2 mb-2">
                                                                    <ChartPieIcon size="18px" color="hsl(158 60% 42%)" />
                                                                    <h6 className="font-weight-bold">Response Distribution</h6>
                                                                </div>

                                                                {responder.answers ? getResponseDistribution(JSON.parse(responder.answers).sections.flatMap(s => s.questions)).map(item => (
                                                                    <div key={item.score} className="mb-2">
                                                                        <div className="d-flex justify-content-between mb-1">
                                                                            <span>{item.label}</span>
                                                                            <span>
                                                                                {item.count} ({item.percentage}%)
                                                                            </span>
                                                                        </div>

                                                                        <ProgressBar now={item.percentage} min={0} max={100} style={{ height: "0.5rem" }} />
                                                                    </div>
                                                                )) : <p className="text-muted">No analysis available.</p>}
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            </> : 
                                            <div dangerouslySetInnerHTML={{__html: assignmentModal.data.templateHtml}}></div>
                                        }
                                    </div>
                                </>
                            )
                        }
                    </Modal.Body>
                </Modal>
            }

            {
                submissionModal.show && (
                    <Modal show={submissionModal.show} onHide={closeModal} centered size="lg" dialogClassName="submission-modal">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <div>
                                    <div className="fw-bold d-flex align-items-center gap-2">
                                        <UserIcon width="25px" />Submission Feedback Response
                                    </div>
                                </div>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className='d-flex flex-column gap-3 p-3'>
                                {
                                    submissionModal.data.questions.map((item, index) => (
                                        <div key={index} className='card-view d-flex flex-column gap-2'>
                                            <div className='d-flex gap-2'>
                                                <div className='fw-bold'>Q{index + 1}:</div>
                                                <div className='text-break' dangerouslySetInnerHTML={{__html: item.questionname}}></div>
                                            </div>
                                            <div className={`d-flex gap-2 ${item.questiontype === QUESTION_TYPE.CODING && 'align-items-center'}`}>
                                                <div>Answer:</div>
                                                {
                                                    item.questiontype === QUESTION_TYPE.DESCRIPTIVE && (
                                                        <div dangerouslySetInnerHTML={{__html: item.singleinputanswer}}></div>
                                                    )
                                                }
                                                {
                                                    (item.questiontype === QUESTION_TYPE.MULTICHOICE || item.questiontype === QUESTION_TYPE.TRUEORFALSE) && (
                                                        <div>{item.options.join(', ')}</div>
                                                    )
                                                }
                                                {
                                                    item.questiontype === QUESTION_TYPE.CODING && (
                                                        <CodeViewer code={item.singleinputanswer} language={item.language?.toLowerCase()} />
                                                    )
                                                }
                                                {
                                                    item.questiontype === QUESTION_TYPE.RATING && (
                                                        <div>{item.ratinganswer}</div>
                                                    )
                                                }
                                            </div>
                                            {
                                                Array.isArray(item.attachments) && item.attachments.length > 0 ?
                                                <div className='d-flex flex-column gap-2'>
                                                    <div className='fw-bold'>Uploaded Assets</div>
                                                    {renderImageFromView(item.attachments, null, false, true)}
                                                </div> : null
                                            }
                                        </div>
                                    ))
                                }
                                {
                                    submissionModal.data.pdf_file && (
                                        <div className='d-flex align-items-center gap-2 uploaded-file' onClick={() => {
                                            window.open(submissionModal.data.pdf_file, '_blank');
                                        }}>
                                            <FileTextIcon />
                                            <div className='fw-bold'>A File has been uploded. Please click to view it.</div>
                                        </div>
                                    )
                                }
                            </div>
                        </Modal.Body>
                    </Modal>
                )
            }
        </div>
    );
};

export default AssignmentView;
